import React, { useCallback, useMemo, useRef } from "react"

import {
  FEATURE_FLAGS,
  useOrganizationFeatureFlagScope,
  usePostHydrationFeatureFlagValue,
} from "@/feature-flags"
import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"

import { JsonEditor } from "@/components/JsonEditor"
import { FieldLabel } from "@/components/shared/forms/FieldLabel"
import {
  DeepgramRealtimeFields,
  GeminiRealtimeFields,
  GrokRealtimeFields,
  OpenAIRealtimeFields,
} from "@/components/shared/forms/SimulatorFormFields"
import { SimulatorType } from "@/types/api/SimulatorType"
import type { SimulationMetadata } from "@/types/backend-contracts"
import type { FormData } from "@/types/FormData"
import type { RequiredKey } from "@/types/SimulatorConfig"
import {
  isOpenAIRealtimeApiKeySource,
  OPENAI_REALTIME_API_KEY_SOURCE,
} from "@/utils/simulation/simulatorFields"
import { stringifyUnknownValue } from "@/utils/stringifyUnknownValue"

import {
  getRequiredFieldsForSimulator,
  populateDefaultMetadataForSimulator,
} from "../utils/simulatorConfiguration"
import { AgentPreviewSection } from "./AgentPreviewSection"
import type { AgentTestConnectionConfig } from "./AgentTestConnectionButton"
import {
  createOpenAIRealtimeModelUpdate,
  getOutboundEndpointDisplay,
  getOutboundEndpointSync,
} from "./simulator-metadata-semantics"

type SimulatorMetadataUpdate = Partial<SimulationMetadata>

interface AgentPreviewWrapperProps {
  formData: FormData
  onFormDataChange: (data: FormData) => void
  testConnection?: AgentTestConnectionConfig | null
}

const parseOptionalFloat = (value: unknown): number | undefined => {
  const parsed = Number.parseFloat(stringifyUnknownValue(value))
  return Number.isFinite(parsed) ? parsed : undefined
}

const parseOptionalInt = (value: unknown): number | undefined => {
  const parsed = Number.parseInt(stringifyUnknownValue(value), 10)
  return Number.isFinite(parsed) ? parsed : undefined
}

// Renders a JSON metadata value (stored as an object/array or a JSON string)
// as an editable JSON string for the Deepgram free-form blob fields.
const jsonMetadataToEditableString = (value: unknown): string => {
  if (value === undefined || value === null || value === "") {
    return ""
  }
  if (typeof value === "string") {
    return value
  }
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return ""
  }
}

const DEEPGRAM_REALTIME_FIELD_KEYS = new Set([
  "deepgram_realtime_api_key",
  "deepgram_realtime_prompt",
  "deepgram_realtime_functions",
  "deepgram_realtime_settings",
  "simulation_timeout_seconds",
])

export const getAdditionalDeepgramRequiredKeys = (
  requiredKeys: RequiredKey[],
): RequiredKey[] =>
  requiredKeys.filter(({ key }) => !DEEPGRAM_REALTIME_FIELD_KEYS.has(key))

export const AgentPreviewWrapper: React.FC<AgentPreviewWrapperProps> = ({
  formData,
  onFormDataChange,
  testConnection,
}) => {
  const latestFormDataRef = useRef(formData)
  latestFormDataRef.current = formData
  const simulatorType = formData.model_type as SimulatorType
  // Resolve the confidential-model flag through the hydration-aware, organization-scoped
  // path. During an organization switch the scope is not ready while flags reload, so the
  // selector stays hidden instead of briefly serving the previous org's cached browser value.
  const gptLiveAlphaFlagValue = usePostHydrationFeatureFlagValue(
    FEATURE_FLAGS.OPENAI_GPT_LIVE_ALPHA,
  )
  const { isReady: isFeatureFlagScopeReady } = useOrganizationFeatureFlagScope()
  const showGptLiveAlpha =
    gptLiveAlphaFlagValue === true && isFeatureFlagScopeReady
  const simulationMetadata: SimulationMetadata = useMemo(
    () => ({
      ...formData.metadata,
      ...(formData.phone_number && { phone_number: formData.phone_number }),
      // Outbound voice persists its trigger URL on the top-level `endpoint`
      // field; `metadata.trigger_call_endpoint` is backend-derived and is not
      // reliably round-tripped through a save. Surface the top-level endpoint
      // so the form field reflects the saved value on reload.
      ...getOutboundEndpointDisplay(simulatorType, formData.endpoint),
    }),
    [
      formData.metadata,
      formData.phone_number,
      formData.endpoint,
      simulatorType,
    ],
  )

  const requiredKeys = useMemo(() => {
    return getRequiredFieldsForSimulator(simulatorType)
  }, [simulatorType])

  const handleSimulatorTypeChange = useCallback(
    (value: string) => {
      const newSimulatorType = value as SimulatorType
      if (newSimulatorType === simulatorType) {
        return
      }

      const defaultMetadata =
        populateDefaultMetadataForSimulator(newSimulatorType)

      onFormDataChange({
        ...formData,
        model_type: value,
        metadata: defaultMetadata,
        phone_number: "",
      })
    },
    [formData, onFormDataChange, simulatorType],
  )

  const handleMetadataUpdate = useCallback(
    (metadataUpdate: SimulatorMetadataUpdate) => {
      const currentFormData = latestFormDataRef.current
      const { phone_number, ...otherMetadata } = metadataUpdate
      // Outbound voice persists its webhook URL in the top-level `endpoint`
      // field (the backend syncs endpoint -> metadata.trigger_call_endpoint).
      // The form only writes trigger_call_endpoint into metadata, so mirror it
      // back onto formData.endpoint or it is dropped on save.
      const outboundEndpoint = getOutboundEndpointSync(
        simulatorType,
        metadataUpdate,
      )

      const updatedFormData = {
        ...currentFormData,
        ...(phone_number !== undefined &&
          typeof phone_number === "string" && { phone_number }),
        ...(outboundEndpoint !== undefined && { endpoint: outboundEndpoint }),
        metadata: {
          ...currentFormData.metadata,
          ...otherMetadata,
        },
      }
      latestFormDataRef.current = updatedFormData
      onFormDataChange(updatedFormData)
    },
    [onFormDataChange, simulatorType],
  )

  const handleOpenAIRealtimeApiKeySourceChange = useCallback(
    (apiKeySource: string) => {
      if (!isOpenAIRealtimeApiKeySource(apiKeySource)) {
        return
      }

      const currentFormData = latestFormDataRef.current
      const metadata: SimulationMetadata = {
        ...currentFormData.metadata,
        openai_realtime_api_key_source: apiKeySource,
      }
      if (apiKeySource === OPENAI_REALTIME_API_KEY_SOURCE.COVAL_MANAGED) {
        delete metadata.openai_realtime_api_key
      }

      const updatedFormData = {
        ...currentFormData,
        metadata,
      }
      latestFormDataRef.current = updatedFormData
      onFormDataChange(updatedFormData)
    },
    [onFormDataChange],
  )

  const renderRequiredKeys = useMemo(() => {
    const renderConfiguredRequiredKeys = (keys: RequiredKey[]) => {
      if (keys.length === 0) {
        return null
      }

      return (
        <div className="space-y-6">
          {keys.map((required_key: RequiredKey) => {
            const rawValue = simulationMetadata[required_key.key]

            // Determine if this field should use JsonEditor
            // Use JsonEditor if: field key contains "json", or current value is an object
            const shouldUseJsonEditor =
              required_key.key.toLowerCase().includes("json") ||
              (rawValue !== undefined &&
                typeof rawValue === "object" &&
                rawValue !== null &&
                !Array.isArray(rawValue))

            // For JSON-like fields (e.g. initialization_json), render objects as JSON strings
            const computedValue =
              rawValue !== undefined
                ? typeof rawValue === "object"
                  ? JSON.stringify(rawValue)
                  : stringifyUnknownValue(rawValue)
                : required_key.default_value || ""

            // Get the current value for JsonEditor - convert to object if needed
            // If the value is stored as a string (from database), parse it to an object
            // If it's already an object, use it directly
            const jsonEditorValue = shouldUseJsonEditor
              ? rawValue !== undefined && rawValue !== null
                ? typeof rawValue === "string"
                  ? (() => {
                      try {
                        return JSON.parse(rawValue) as Record<string, unknown>
                      } catch {
                        return {}
                      }
                    })()
                  : typeof rawValue === "object" && !Array.isArray(rawValue)
                    ? (rawValue as Record<string, unknown>)
                    : {}
                : {}
              : {}

            const fieldId = `required-key-${required_key.key}`
            const fieldLabel = required_key.label || required_key.key

            return (
              <div key={required_key.key} className="space-y-2">
                <FieldLabel htmlFor={fieldId} tooltip={required_key.tooltip}>
                  {fieldLabel}
                  {required_key.optional ? (
                    " (Optional)"
                  ) : (
                    <span className="text-coval-danger-text"> *</span>
                  )}
                </FieldLabel>
                {required_key.options ? (
                  <Select
                    value={(() => {
                      const currentValue =
                        (simulationMetadata[required_key.key] as string) ||
                        required_key.default_value ||
                        ""
                      if (required_key.option_values) {
                        const displayName = Object.entries(
                          required_key.option_values,
                        ).find(
                          ([_, actualValue]) => actualValue === currentValue,
                        )?.[0]
                        return displayName || currentValue
                      }
                      return currentValue
                    })()}
                    onValueChange={(selectedKey) => {
                      const actualValue =
                        required_key.option_values?.[selectedKey] || selectedKey
                      handleMetadataUpdate({
                        [required_key.key]: actualValue,
                      })
                    }}
                  >
                    <SelectTrigger id={fieldId} aria-label={fieldLabel}>
                      <SelectValue placeholder="Select an option..." />
                    </SelectTrigger>
                    <SelectContent>
                      {required_key.options.map((option: string) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : shouldUseJsonEditor ? (
                  <JsonEditor
                    ariaLabel={`${fieldLabel} JSON`}
                    value={jsonEditorValue}
                    onChange={(value) => {
                      handleMetadataUpdate({
                        [required_key.key]:
                          value && typeof value === "object"
                            ? JSON.stringify(value)
                            : value,
                      })
                    }}
                    height="300px"
                    allowEmpty={required_key.optional}
                  />
                ) : (
                  <Input
                    id={fieldId}
                    type="text"
                    placeholder="Enter a value..."
                    aria-label={fieldLabel}
                    value={computedValue}
                    onChange={(e) => {
                      handleMetadataUpdate({
                        [required_key.key]: e.target.value,
                      })
                    }}
                  />
                )}
                {required_key.help && (
                  <p className="text-xs text-coval-text-muted">
                    {required_key.help}
                  </p>
                )}
              </div>
            )
          })}
        </div>
      )
    }

    if (simulatorType === SimulatorType.GEMINI_REALTIME) {
      return (
        <GeminiRealtimeFields
          apiKey={(simulationMetadata.gemini_realtime_api_key as string) || ""}
          instructions={
            (simulationMetadata.gemini_realtime_instructions as string) || ""
          }
          model={(simulationMetadata.gemini_realtime_model as string) || ""}
          voice={(simulationMetadata.gemini_realtime_voice as string) || ""}
          temperature={
            simulationMetadata.gemini_realtime_temperature != null
              ? parseOptionalFloat(
                  simulationMetadata.gemini_realtime_temperature,
                )
              : undefined
          }
          timeoutSeconds={
            simulationMetadata.simulation_timeout_seconds != null
              ? parseOptionalInt(simulationMetadata.simulation_timeout_seconds)
              : undefined
          }
          onApiKeyChange={(value) =>
            handleMetadataUpdate({ gemini_realtime_api_key: value })
          }
          onInstructionsChange={(value) =>
            handleMetadataUpdate({ gemini_realtime_instructions: value })
          }
          onModelChange={(value) =>
            handleMetadataUpdate({ gemini_realtime_model: value })
          }
          onVoiceChange={(value) =>
            handleMetadataUpdate({ gemini_realtime_voice: value })
          }
          onTemperatureChange={(value) =>
            handleMetadataUpdate({
              gemini_realtime_temperature:
                value !== undefined ? value.toString() : "",
            })
          }
          onTimeoutSecondsChange={(value) =>
            handleMetadataUpdate({
              simulation_timeout_seconds:
                value !== undefined ? value.toString() : "",
            })
          }
        />
      )
    }

    if (simulatorType === SimulatorType.GROK_REALTIME) {
      return (
        <GrokRealtimeFields
          apiKey={(simulationMetadata.grok_realtime_api_key as string) || ""}
          instructions={
            (simulationMetadata.grok_realtime_instructions as string) || ""
          }
          model={(simulationMetadata.grok_realtime_model as string) || ""}
          voice={(simulationMetadata.grok_realtime_voice as string) || ""}
          timeoutSeconds={
            simulationMetadata.simulation_timeout_seconds != null
              ? parseOptionalInt(simulationMetadata.simulation_timeout_seconds)
              : undefined
          }
          onApiKeyChange={(value) =>
            handleMetadataUpdate({ grok_realtime_api_key: value })
          }
          onInstructionsChange={(value) =>
            handleMetadataUpdate({ grok_realtime_instructions: value })
          }
          onModelChange={(value) =>
            handleMetadataUpdate({ grok_realtime_model: value })
          }
          onVoiceChange={(value) =>
            handleMetadataUpdate({ grok_realtime_voice: value })
          }
          onTimeoutSecondsChange={(value) =>
            handleMetadataUpdate({
              simulation_timeout_seconds:
                value !== undefined ? value.toString() : "",
            })
          }
        />
      )
    }

    if (simulatorType === SimulatorType.DEEPGRAM_REALTIME) {
      const configuredFields = getAdditionalDeepgramRequiredKeys(requiredKeys)

      return (
        <div className="space-y-6">
          <DeepgramRealtimeFields
            apiKey={
              (simulationMetadata.deepgram_realtime_api_key as string) || ""
            }
            prompt={
              (simulationMetadata.deepgram_realtime_prompt as string) || ""
            }
            functions={jsonMetadataToEditableString(
              simulationMetadata.deepgram_realtime_functions,
            )}
            settings={jsonMetadataToEditableString(
              simulationMetadata.deepgram_realtime_settings,
            )}
            timeoutSeconds={
              simulationMetadata.simulation_timeout_seconds != null
                ? parseOptionalInt(
                    simulationMetadata.simulation_timeout_seconds,
                  )
                : undefined
            }
            requiredFieldKeys={
              new Set(
                requiredKeys
                  .filter(({ optional }) => optional !== true)
                  .map(({ key }) => key),
              )
            }
            onApiKeyChange={(value) =>
              handleMetadataUpdate({ deepgram_realtime_api_key: value })
            }
            onPromptChange={(value) =>
              handleMetadataUpdate({ deepgram_realtime_prompt: value })
            }
            onFunctionsChange={(value) =>
              handleMetadataUpdate({ deepgram_realtime_functions: value })
            }
            onSettingsChange={(value) =>
              handleMetadataUpdate({ deepgram_realtime_settings: value })
            }
            onTimeoutSecondsChange={(value) =>
              handleMetadataUpdate({
                simulation_timeout_seconds:
                  value !== undefined ? value.toString() : "",
              })
            }
          />
          {renderConfiguredRequiredKeys(configuredFields)}
        </div>
      )
    }

    if (simulatorType === SimulatorType.OPENAI_REALTIME) {
      return (
        <OpenAIRealtimeFields
          showGptLiveAlpha={showGptLiveAlpha}
          apiKey={(simulationMetadata.openai_realtime_api_key as string) || ""}
          apiKeySource={
            isOpenAIRealtimeApiKeySource(
              simulationMetadata.openai_realtime_api_key_source,
            )
              ? simulationMetadata.openai_realtime_api_key_source
              : OPENAI_REALTIME_API_KEY_SOURCE.CUSTOMER_PROVIDED
          }
          instructions={
            (simulationMetadata.openai_realtime_instructions as string) || ""
          }
          model={(simulationMetadata.openai_realtime_model as string) || ""}
          voice={(simulationMetadata.openai_realtime_voice as string) || ""}
          temperature={
            simulationMetadata.openai_realtime_temperature != null
              ? parseOptionalFloat(
                  simulationMetadata.openai_realtime_temperature,
                )
              : undefined
          }
          timeoutSeconds={
            simulationMetadata.simulation_timeout_seconds != null
              ? parseOptionalInt(simulationMetadata.simulation_timeout_seconds)
              : undefined
          }
          webSearchEnabled={
            simulationMetadata.openai_realtime_web_search_enabled === true ||
            simulationMetadata.openai_realtime_web_search_enabled === "true"
          }
          onApiKeyChange={(value) =>
            handleMetadataUpdate({ openai_realtime_api_key: value })
          }
          onApiKeySourceChange={handleOpenAIRealtimeApiKeySourceChange}
          onInstructionsChange={(value) =>
            handleMetadataUpdate({ openai_realtime_instructions: value })
          }
          onModelChange={(value, disableWebSearch) =>
            handleMetadataUpdate(
              createOpenAIRealtimeModelUpdate(
                value,
                simulationMetadata,
                disableWebSearch,
              ),
            )
          }
          onVoiceChange={(value) =>
            handleMetadataUpdate({ openai_realtime_voice: value })
          }
          onTemperatureChange={(value) =>
            handleMetadataUpdate({
              openai_realtime_temperature:
                value !== undefined ? value.toString() : "",
            })
          }
          onTimeoutSecondsChange={(value) =>
            handleMetadataUpdate({
              simulation_timeout_seconds:
                value !== undefined ? value.toString() : "",
            })
          }
          onWebSearchEnabledChange={(value) =>
            handleMetadataUpdate({ openai_realtime_web_search_enabled: value })
          }
        />
      )
    }

    return renderConfiguredRequiredKeys(requiredKeys)
  }, [
    simulatorType,
    requiredKeys,
    simulationMetadata,
    handleMetadataUpdate,
    handleOpenAIRealtimeApiKeySourceChange,
    showGptLiveAlpha,
  ])

  return (
    <AgentPreviewSection
      simulatorType={simulatorType}
      simulationMetadata={simulationMetadata}
      onSimulatorTypeChange={handleSimulatorTypeChange}
      onMetadataUpdate={handleMetadataUpdate}
      renderRequiredKeys={() => renderRequiredKeys}
      testConnection={testConnection}
    />
  )
}
